import {
    Box,
    Button,
    Container,
    Divider,
    Grid,
    Menu,
    MenuItem,
    Typography,
    useMediaQuery,
    useTheme,
} from '@material-ui/core';
import { ReactComponent as AzerbaijaniFlagIcon } from 'assets/icons/flags/az.svg';
import { ReactComponent as EnglishFlagIcon } from 'assets/icons/flags/en.svg';
import { ReactComponent as RussianFlagIcon } from 'assets/icons/flags/ru.svg';
import { Link } from 'components';
import { logoTextBlack, logoTextWhite } from 'config';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FooterSectionItem } from './components/footer-section-item';
import { SocialButtons } from './components/social-buttons';
import { useStyle } from './footer.style';

export const Footer: React.FC = () => {
    const classes = useStyle();
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleClose = (): void => setAnchorEl(null);

    const { isLoggedIn, openModal, isEmployer, isAdmin } = useAuth();
    const { isDarkMode, language = 'az', setLanguage } = useSettings();

    const languages = {
        az: { name: 'Azərbaycanca', icon: AzerbaijaniFlagIcon },
        en: { name: 'English', icon: EnglishFlagIcon },
        ru: { name: 'Русский', icon: RussianFlagIcon },
    };

    const LanguageIcon = languages[language].icon;

    return (
        <footer id="footer" className={classes.container}>
            <Divider />

            <Container maxWidth="lg">
                <div className={classes.middleSection}>
                    <div className={classes.content}>
                        <div className={classes.middleSectionContent}>
                            <Grid container spacing={isMobile ? 2 : 4} className={classes.middleLeftSide}>
                                <Grid item xs={12} sm={6} md={3} className={classes.list}>
                                    <FooterSectionItem title={t('footer:forCandidates')}>
                                        <Link link to={'/jobs'}>
                                            <Typography variant="body1" color="textSecondary">
                                                {t('footer:jobs')}
                                            </Typography>
                                        </Link>
                                        <Link
                                            link
                                            onClick={() => !isLoggedIn && openModal()}
                                            to={isLoggedIn ? '/d/profile' : '#'}
                                            style={{
                                                display: !isLoggedIn || (!isEmployer && !isAdmin) ? 'block' : 'none',
                                            }}
                                        >
                                            <Typography variant="body1" color="textSecondary">
                                                {t('footer:addResume')}
                                            </Typography>
                                        </Link>
                                        <Link
                                            link
                                            onClick={() => !isLoggedIn && openModal()}
                                            to={isLoggedIn ? '/d/bookmarks' : '#'}
                                            style={{
                                                display: !isLoggedIn || (!isEmployer && !isAdmin) ? 'block' : 'none',
                                            }}
                                        >
                                            <Typography variant="body1" color="textSecondary">
                                                {t('footer:myBookmarks')}
                                            </Typography>
                                        </Link>
                                        <Link
                                            style={{
                                                display: isLoggedIn ? 'none' : 'block',
                                            }}
                                            link
                                            to={'/sign-up-jobseeker'}
                                        >
                                            <Typography variant="body1" color="textSecondary">
                                                {t('auth:registerAsJobseeker')}
                                            </Typography>
                                        </Link>
                                    </FooterSectionItem>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                    className={classes.list}
                                    style={{ display: !isLoggedIn || isEmployer ? 'flex' : 'none' }}
                                >
                                    <FooterSectionItem title={t('footer:forEmployers')}>
                                        <Link
                                            link
                                            onClick={() => !isLoggedIn && openModal()}
                                            to={isLoggedIn ? '/d/candidates' : '#'}
                                        >
                                            <Typography variant="body1" color="textSecondary">
                                                {t('footer:browseCandidates')}
                                            </Typography>
                                        </Link>
                                        <Link
                                            link
                                            onClick={() => !isLoggedIn && openModal()}
                                            to={isLoggedIn ? '/d/job/new' : '#'}
                                        >
                                            <Typography variant="body1" color="textSecondary">
                                                {t('footer:postAJob')}
                                            </Typography>
                                        </Link>
                                        <Link link to="/pricing">
                                            <Typography variant="body1" color="textSecondary">
                                                {t('footer:plansAndPricing')}
                                            </Typography>
                                        </Link>
                                        <Link
                                            style={{
                                                display: isLoggedIn ? 'none' : 'block',
                                            }}
                                            link
                                            to={'/sign-up-employer'}
                                        >
                                            <Typography variant="body1" color="textSecondary">
                                                {t('auth:registerAsEmployer')}
                                            </Typography>
                                        </Link>
                                    </FooterSectionItem>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3} className={classes.list}>
                                    <FooterSectionItem title={t('footer:helpfulLinks')}>
                                        <Link link to={'/privacy-policy'}>
                                            <Typography variant="body1" color="textSecondary">
                                                {t('footer:privacyPolicy')}
                                            </Typography>
                                        </Link>
                                        <Link link to={'/jobs'}>
                                            <Typography variant="body1" color="textSecondary">
                                                {t('footer:termsOfUse')}
                                            </Typography>
                                        </Link>
                                    </FooterSectionItem>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3} className={classes.list}>
                                    <FooterSectionItem title={t('footer:contact')}>
                                        <a href="tel:+994554429193">
                                            <Typography variant="body1" color="textSecondary">
                                                +994 55 442 91 93
                                            </Typography>
                                        </a>
                                        <a href="mailto:info@staffy.az">
                                            <Typography variant="body1" color="textSecondary">
                                                info@staffy.az
                                            </Typography>
                                        </a>
                                    </FooterSectionItem>
                                </Grid>
                            </Grid>
                        </div>
                    </div>
                </div>
                <Divider />
                <Box className={classes.bottomSection}>
                    <Box className={classes.info}>
                        <img
                            style={{ width: 82, height: '30.3px' }}
                            src={isDarkMode ? logoTextWhite : logoTextBlack}
                            alt="logo"
                        />
                        <Typography color="textSecondary" className={classes.copyright} variant="body2">
                            © Staffy LLC. 2021
                        </Typography>
                    </Box>
                    <Box className={classes.socials}>
                        <SocialButtons />
                        <div className={classes.languageWrapper}>
                            <Button className={classes.language} onClick={(e) => setAnchorEl(e.currentTarget)}>
                                <LanguageIcon width={28} height={28} style={{ marginRight: 6 }} />
                                {languages[language].name}
                            </Button>
                        </div>
                        <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleClose}>
                            {Object.keys(languages).map((key) => (
                                <MenuItem
                                    key={key}
                                    value="en"
                                    selected={language === key}
                                    onClick={() => {
                                        i18n.changeLanguage(key);
                                        setLanguage(key);
                                        handleClose();
                                    }}
                                >
                                    {languages[key].name}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Box>
            </Container>
        </footer>
    );
};
